// Helper to convert English numbers to Bengali
const toBn = (n: number | string): string => {
    return String(n).replace(/[0-9]/g, (d) => '০১২৩৪৫৬৭৮৯'[parseInt(d)]);
};

// Map of English numeric Day to precise Bengali suffix
const getBnSuffix = (day: number | string): string => {
    // Map Bengali digits back to English for safe parsing
    const strDay = String(day).replace(/[০-৯]/g, (d) => String('০১২৩৪৫৬৭৮৯'.indexOf(d)));
    const d = parseInt(strDay);
    if (isNaN(d)) return 'ই'; // Safe fallback

    if (d === 1) return 'লা';
    if (d === 2 || d === 3) return 'রা';
    if (d === 4) return 'ঠা';
    if (d >= 5 && d <= 18) return 'ই';
    return 'শে';
};

// Precise Dhaka Time Components extraction
const getDhakaParts = (date: Date) => {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Dhaka',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        hour12: false
    });
    const parts = formatter.formatToParts(date);
    const get = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0');
    return { year: get('year'), month: get('month') - 1, day: get('day'), hour: get('hour') };
};

// Manual Bengali Month Calculation (Bangladesh Standard)
const getBengaliDateParts = (date: Date) => {
    const dp = getDhakaParts(date);
    const year = dp.year;
    const month = dp.month;
    const day = dp.day;

    const bengaliMonths = [
        'বৈশাখ', 'জ্যৈষ্ঠ', 'আষাঢ়', 'শ্রাবণ',
        'ভাদ্র', 'আশ্বিন', 'কার্তিক', 'অগ্রহায়ণ',
        'পৌষ', 'মাঘ', 'ফাল্গুন', 'চৈত্র'
    ];

    // Bangladesh Standard: Boishakh 1 is always April 14
    const startOfBoishakh = new Date(year, 3, 14);
    const todayMidnight = new Date(year, month, day);
    let diffDays = Math.floor((todayMidnight.getTime() - startOfBoishakh.getTime()) / (1000 * 60 * 60 * 24));

    let currentBnYear = year - 593;
    if (diffDays < 0) {
        currentBnYear--;
        const prevYearStart = new Date(year - 1, 3, 14);
        diffDays = Math.floor((todayMidnight.getTime() - prevYearStart.getTime()) / (1000 * 60 * 60 * 24));
    }

    const isLeapYear = (y: number) => (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0);
    // 2019 Revised Bangladesh Standard: 
    // First 6 months (Boishakh-Ashwin) = 31 days. Next 5 (Kartik-Magh) = 30.
    // Falgun is 29 days (30 in leap year). Chaitra is 30.
    const monthLengths = [31, 31, 31, 31, 31, 31, 30, 30, 30, 30, 29, 30];
    // Falgun corresponds to month index 10.
    // But wait, Gregorian Leap year occurs in Feb. (Bengali year ending).
    // The Gregorian year corresponding to Falgun is `year + 1` if we consider Boishakh starting in `year`.
    // Let's use the Gregorian year of the current date for the leap determination.
    const gregorianYear = dp.year;
    if ((gregorianYear % 4 === 0 && gregorianYear % 100 !== 0) || (gregorianYear % 400 === 0)) {
        monthLengths[10] = 30;
    }

    let mIdx = 0;
    while (mIdx < 12 && diffDays >= monthLengths[mIdx]) {
        diffDays -= monthLengths[mIdx];
        mIdx++;
    }

    return {
        day: toBn(diffDays + 1),
        month: bengaliMonths[mIdx],
        year: toBn(currentBnYear)
    };
};

export const getFormattedDates = (): { staticDay: string, sliderItems: string[] } => {
    const rawNow = new Date();
    const dp = getDhakaParts(rawNow);

    // Static day – e.g. “আজ শনিবার”
    const weekday = rawNow.toLocaleDateString('bn-BD', { weekday: 'long', timeZone: 'Asia/Dhaka' });
    const staticDay = `আজ ${weekday}`;

    // Bengali date
    const bnParts = getBengaliDateParts(rawNow);
    const bnDayClean = bnParts.day.replace(/[ইশেলা]/g, '');
    const bnString = `${bnDayClean}${getBnSuffix(bnDayClean)} ${bnParts.month} ${bnParts.year} বঙ্গাব্দ`;

    // Gregorian (English) date in Bengali numerals
    const enString = `${toBn(dp.day)}${getBnSuffix(dp.day)} ${rawNow.toLocaleDateString('bn-BD', { month: 'long', timeZone: 'Asia/Dhaka' })}, ${toBn(dp.year)} খ্রিস্টাব্দ`;

    // Hijri (islamic-civil tabular) — matches common BD sources like bangladatetoday.com
    const hijriMonths = [
        'মহররম', 'সফর', 'রবিউল আউয়াল', 'রবিউস সানি', 'জমাদিউল আউয়াল', 'জমাদিউস সানি',
        'রজব', 'শাবান', 'রমজান', 'শাওয়াল', 'জিলকদ', 'জিলহজ'
    ];

    let hijriString = '';
    try {
        const formatter = new Intl.DateTimeFormat('en-u-ca-islamic-civil', {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
            timeZone: 'Asia/Dhaka',
        });
        const parts = formatter.formatToParts(rawNow);
        const hDay = parts.find(p => p.type === 'day')?.value || '1';
        const hMonthIdx = parseInt(parts.find(p => p.type === 'month')?.value || '1') - 1;
        const hYear = parts.find(p => p.type === 'year')?.value || '';

        const bnHDay = toBn(hDay);
        const bnHMonth = hijriMonths[hMonthIdx];
        const bnHYear = toBn(hYear);

        hijriString = `${bnHDay}${getBnSuffix(hDay)} ${bnHMonth} ${bnHYear} হিজরি`;
    } catch (_) {
        hijriString = '';
    }

    return {
        staticDay,
        sliderItems: [bnString, enString, ...(hijriString ? [hijriString] : [])]
    };
};

export const formatBengaliDateTime = (dateStr?: string | Date, isManual: boolean = false) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);

    if (!isManual) {
        // Apply the 15‑minute back‑date rule ONLY for crawled news
        date.setMinutes(date.getMinutes() - 15);
    }

    const formatted = date.toLocaleString('bn-BD', {
        timeZone: 'Asia/Dhaka',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
    }).replace(/am/i, 'পূর্বাহ্ণ').replace(/pm/i, 'অপরাহ্ণ');

    // Ensure all English digits are converted to Bengali digits for safety
    return toBn(formatted);
};

export const formatRelativeBengaliDateTime = (dateStr?: string | Date, isManual: boolean = false): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);

    if (!isManual) {
        // Apply the 15‑minute back‑date rule ONLY for crawled news
        date.setMinutes(date.getMinutes() - 15);
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const oneDayMs = 24 * 60 * 60 * 1000;

    // If future or >= 24 hours, fall back to full absolute Bengali date string
    if (diffMs < 0 || diffMs >= oneDayMs) {
        return formatBengaliDateTime(dateStr, isManual);
    }

    const diffMins = Math.floor(diffMs / (60 * 1000));
    if (diffMins < 1) {
        return 'এইমাত্র';
    }
    if (diffMins < 60) {
        return `${toBn(diffMins)} মিনিট আগে`;
    }

    const diffHours = Math.floor(diffMs / (60 * 60 * 1000));
    return `${toBn(diffHours)} ঘণ্টা আগে`;
};
